import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

function YouTubeEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-100">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        loading="lazy"
      />
    </div>
  );
}

interface NewsItem {
  mediaKey: string;
  dateKey: string;
  titleKey: string;
  titleEnKey: string;
  descKey: string;
  descEnKey: string;
  linkKey: string;
}

interface PhotoItem {
  mediaKey: string;
  dateKey: string;
  titleKey: string;
  titleEnKey: string;
  descKey: string;
  descEnKey: string;
  linkKey: string;
  imageUrl: string;
}

const NAVY = '#082B5F';
const NAVY_HOVER = '#0A3A7A';
const GOLD = '#C8A45D';

export default function Media() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const title = isZh
    ? '媒體報導｜國際扶輪和平意象｜澎湖和平意象紀念碑'
    : 'Media Coverage | Rotary International Peace Monument | Penghu Peace Monument';
  const description = isZh
    ? '彙整國際扶輪和平意象的媒體報導、影音報導與新聞圖片。中央社、自由時報、澎湖日報、Yahoo新聞等媒體共同見證澎湖和平新地標的誕生。'
    : 'Media coverage, video reports, and photo news about the Rotary International Peace Monument. Witnessed by CNA, Liberty Times, Penghu Daily, Yahoo News, and other media outlets.';
  const keywords = isZh
    ? '媒體報導, 國際扶輪, 和平意象, 澎湖新聞, 中央社, 自由時報, 公共藝術'
    : 'Media Coverage, Rotary International, Peace Monument, Penghu News, CNA, Public Art, Taiwan Landmark';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    url: `${siteUrl}/media`,
    description,
    inLanguage: isZh ? 'zh-TW' : 'en',
    isPartOf: {
      '@type': 'WebSite',
      url: `${siteUrl}/`,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: isZh ? '影音報導' : 'Video Reports',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: isZh ? '新聞報導' : 'News Coverage',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: isZh ? '媒體新聞圖片' : 'Photo News',
        },
      ],
    },
  };

  const videos = [
    {
      id: 'wfo4qahaZes',
      title: t('media.card1.title'),
      titleEn: t('media.card1.titleEn'),
      desc: t('media.card1.desc'),
    },
    {
      id: 'cxuaOZg1OYA',
      title: t('media.card2.title'),
      titleEn: t('media.card2.titleEn'),
      desc: t('media.card2.desc'),
    },
  ];

  const newsItems: NewsItem[] = [
    {
      mediaKey: 'media.news1.media',
      dateKey: 'media.news1.date',
      titleKey: 'media.news1.title',
      titleEnKey: 'media.news1.titleEn',
      descKey: 'media.news1.desc',
      descEnKey: 'media.news1.descEn',
      linkKey: 'media.news1.link',
    },
    {
      mediaKey: 'media.news2.media',
      dateKey: 'media.news2.date',
      titleKey: 'media.news2.title',
      titleEnKey: 'media.news2.titleEn',
      descKey: 'media.news2.desc',
      descEnKey: 'media.news2.descEn',
      linkKey: 'media.news2.link',
    },
    {
      mediaKey: 'media.news3.media',
      dateKey: 'media.news3.date',
      titleKey: 'media.news3.title',
      titleEnKey: 'media.news3.titleEn',
      descKey: 'media.news3.desc',
      descEnKey: 'media.news3.descEn',
      linkKey: 'media.news3.link',
    },
    {
      mediaKey: 'media.news4.media',
      dateKey: 'media.news4.date',
      titleKey: 'media.news4.title',
      titleEnKey: 'media.news4.titleEn',
      descKey: 'media.news4.desc',
      descEnKey: 'media.news4.descEn',
      linkKey: 'media.news4.link',
    },
  ];

  const photoItems: PhotoItem[] = [
    {
      mediaKey: 'media.photo1.media',
      dateKey: 'media.photo1.date',
      titleKey: 'media.photo1.title',
      titleEnKey: 'media.photo1.titleEn',
      descKey: 'media.photo1.desc',
      descEnKey: 'media.photo1.descEn',
      linkKey: 'media.photo1.link',
      imageUrl:
        'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/a780486d58bb20f8682dc045b95bf759.jpeg',
    },
    {
      mediaKey: 'media.photo2.media',
      dateKey: 'media.photo2.date',
      titleKey: 'media.photo2.title',
      titleEnKey: 'media.photo2.titleEn',
      descKey: 'media.photo2.desc',
      descEnKey: 'media.photo2.descEn',
      linkKey: 'media.photo2.link',
      imageUrl:
        'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/d68fd50bd005fa6dccaddda75c9ca48f.jpeg',
    },
    {
      mediaKey: 'media.photo3.media',
      dateKey: 'media.photo3.date',
      titleKey: 'media.photo3.title',
      titleEnKey: 'media.photo3.titleEn',
      descKey: 'media.photo3.desc',
      descEnKey: 'media.photo3.descEn',
      linkKey: 'media.photo3.link',
      imageUrl:
        'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/09e055a941ceb1de91f03160025d32d9.jpeg',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonical="/media"
        schema={schema}
        lang={isZh ? 'zh-TW' : 'en'}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
            <span
              className="inline-block text-xs md:text-sm font-medium tracking-widest uppercase mb-4"
              style={{ color: GOLD }}
            >
              {t('media.label')}
            </span>
            <h1
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4"
              style={{ color: NAVY }}
            >
              {t('media.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-8">
              {t('media.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/videos')}
                className="group flex items-center gap-2 px-6 py-3 text-background-50 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
                style={{ background: NAVY }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = NAVY_HOVER;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = NAVY;
                }}
              >
                {t('media.cta.videos')}
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/')}
                className="group flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
                style={{ color: NAVY, border: `1px solid ${NAVY}` }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = NAVY;
                  el.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.background = 'transparent';
                  el.style.color = NAVY;
                }}
              >
                {t('media.cta.home')}
                <i className="ri-arrow-left-line text-sm group-hover:-translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Video Reports */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span
                className="inline-block text-xs font-medium tracking-widest uppercase mb-3"
                style={{ color: GOLD }}
              >
                {t('media.video.subtitle')}
              </span>
              <h2
                className="font-heading text-2xl md:text-3xl font-bold mb-4"
                style={{ color: NAVY }}
              >
                {t('media.video.title')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                {isZh ? t('media.video.desc') : t('media.video.descEn')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {videos.map((video, index) => (
                <div
                  key={video.id}
                  className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/50 hover:shadow-lg transition-all duration-500"
                >
                  <div className="relative">
                    {index === 0 && (
                      <div
                        className="absolute top-4 left-4 z-10 text-background-50 text-xs font-medium px-3 py-1 rounded-full"
                        style={{ background: NAVY }}
                      >
                        Latest
                      </div>
                    )}
                    <YouTubeEmbed videoId={video.id} title={video.title} />
                  </div>
                  <div className="p-6 md:p-8 text-center">
                    <h3
                      className="font-heading text-lg md:text-xl font-bold mb-2"
                      style={{ color: NAVY }}
                    >
                      {video.title}
                    </h3>
                    <p className="text-sm text-foreground-500 mb-4">
                      {video.titleEn}
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {video.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* News Coverage */}
        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span
                className="inline-block text-xs font-medium tracking-widest uppercase mb-3"
                style={{ color: GOLD }}
              >
                {t('media.news.subtitle')}
              </span>
              <h2
                className="font-heading text-2xl md:text-3xl font-bold mb-4"
                style={{ color: NAVY }}
              >
                {t('media.news.title')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                {isZh ? t('media.news.desc') : t('media.news.descEn')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {newsItems.map((item) => (
                <div
                  key={item.linkKey}
                  className="group bg-background-50 rounded-xl border border-background-200/60 hover:shadow-md transition-all duration-300 hover:-translate-y-1 flex flex-col"
                >
                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <span
                        className="inline-block text-xs font-medium px-2.5 py-1 rounded-full"
                        style={{
                          background: 'rgba(200,164,93,0.12)',
                          color: GOLD,
                        }}
                      >
                        {t(item.mediaKey)}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {t(item.dateKey)}
                      </span>
                    </div>
                    <h3
                      className="font-heading text-base md:text-lg font-bold mb-3 leading-snug"
                      style={{ color: NAVY }}
                    >
                      {isZh ? t(item.titleKey) : t(item.titleEnKey)}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5 flex-1">
                      {isZh ? t(item.descKey) : t(item.descEnKey)}
                    </p>
                    <a
                      href={t(item.linkKey)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300"
                      style={{ color: NAVY }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color = GOLD;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color = NAVY;
                      }}
                    >
                      <span className="whitespace-nowrap">{t('media.readMore')}</span>
                      <i className="ri-external-link-line text-sm" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Photo News */}
        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10 md:mb-14">
              <span
                className="inline-block text-xs font-medium tracking-widest uppercase mb-3"
                style={{ color: GOLD }}
              >
                {t('media.photo.subtitle')}
              </span>
              <h2
                className="font-heading text-2xl md:text-3xl font-bold mb-4"
                style={{ color: NAVY }}
              >
                {t('media.photo.title')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                {isZh ? t('media.photo.desc') : t('media.photo.descEn')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {photoItems.map((item) => (
                <div
                  key={item.linkKey}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full h-48 md:h-52 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={t(item.titleKey)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{
                          background: 'rgba(200,164,93,0.12)',
                          color: GOLD,
                        }}
                      >
                        {t(item.mediaKey)}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {t(item.dateKey)}
                      </span>
                    </div>
                    <h3
                      className="font-heading text-base font-bold mb-2 leading-snug"
                      style={{ color: NAVY }}
                    >
                      {isZh ? t(item.titleKey) : t(item.titleEnKey)}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {isZh ? t(item.descKey) : t(item.descEnKey)}
                    </p>
                    <a
                      href={t(item.linkKey)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300"
                      style={{ color: NAVY }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color = GOLD;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color = NAVY;
                      }}
                    >
                      <span className="whitespace-nowrap">{t('media.readMore')}</span>
                      <i className="ri-external-link-line text-sm" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* More Coming */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-16 h-px" style={{ background: 'rgba(200,164,93,0.30)' }} />
              <div className="w-3 h-3 rotate-45 border" style={{ borderColor: 'rgba(200,164,93,0.40)' }} />
              <div className="w-16 h-px" style={{ background: 'rgba(200,164,93,0.30)' }} />
            </div>
            <h2
              className="font-heading text-2xl md:text-3xl font-bold mb-3"
              style={{ color: NAVY }}
            >
              {t('media.more.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600">
              {t('media.more.subtitle')}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}